import {isUndefined} from 'lodash';
import memoize from 'lru-memoize';

import {
  isExistingModifier,
  isValidValueForModifier,
  ModifiersEnum,
  modifiersSet,
} from './searchModifiersHelpers';

/*
 * Types.
 */

interface SearchBaseToken {
  // `value` could be undefined, e.g. for `from:`.
  value: string | undefined;
}

export interface SearchModifierToken extends SearchBaseToken {
  modifier: ModifiersEnum;
}

export type SearchToken = SearchBaseToken | SearchModifierToken;

export interface SearchQueryPart {
  text: string;
  startOffset: number;
  endOffset: number;
  token: SearchToken | undefined;
}

/*
 * Constants.
 */

const emptySearchQueryPart: SearchQueryPart = {
  text: '',
  startOffset: 0,
  endOffset: 0,
  token: {
    value: '',
  },
};

const WILDCARD_VALUE = '+';

/*
 * Helpers.
 */

/**
 * "                                # opening double-quote…
 * [^"]*                            # …followed by zero or more chars that are not a double-quote…
 * "?                               # …maybe closing double-quote
 */
const doubleQuotesRegExp = '"[^"]*"?';

/**
 * (?:${[...modifiers].join('|')})  # non-capturing group including one modifier
 * (?::\\s*)?                       # maybe non-capturing group including a colon followed by zero or more spaces
 * (                                # maybe non capturing group including:
 *  [^\\s"]+                        #   - anything that's not a space or a double-quote
 * |                                # or
 *  ${doubleQuotesRegExp}           #   - opening double-quote followed by zero or more chars that are not a double-quote and maybe closing double-quote
 * )?
 */
const modifierRegExp = `(?:${[...modifiersSet].join('|')})(?::\\s*)?(?:[^\\s"]+|${doubleQuotesRegExp})?`;

/**
 * /S+                              # anything that is not a whitespace
 */
const anythingButWhitespaceRegExp = '\\S+';

const searchQuerySplitRegExp = new RegExp(
  `(${modifierRegExp}|${doubleQuotesRegExp}|${anythingButWhitespaceRegExp})`,
);

/*
 * Type guards.
 */

export function isSearchModifierToken(token: SearchToken): token is SearchModifierToken {
  return 'modifier' in token && isExistingModifier(token.modifier);
}

/*
 * Helpers.
 */

export function splitSearchQuery(query: string): ReadonlyArray<SearchQueryPart> {
  let currentOffset = 0;
  const parts = query
    .split(searchQuerySplitRegExp)
    .filter((text) => text.length > 0)
    .map((text) => {
      const startOffset = currentOffset;
      const endOffset = currentOffset + text.length;
      currentOffset = endOffset;

      return {
        text,
        startOffset,
        endOffset,
        token: buildSearchToken(text),
      };
    });

  return parts.length > 0 ? parts : [emptySearchQueryPart];
}

export function buildSearchToken(query: string): SearchToken | undefined {
  if (query.trim().length === 0) {
    return undefined;
  }

  if (!query.includes(':')) {
    return {value: query};
  }

  // Isolate potential modifier and value.
  const [modifier, value] = query.split(':');
  const lowerCasedModifier = modifier.toLowerCase();

  if (isExistingModifier(lowerCasedModifier)) {
    return {
      modifier: lowerCasedModifier,
      value: value.trim(),
    };
  }

  return {value: query};
}

export function isWildcardToken(token: SearchToken, query?: string) {
  return (
    isUndefined(query) || !query.length || (!isSearchModifierToken(token) && token.value === WILDCARD_VALUE)
  );
}

export function findActiveToken(query: string, offset = 0): SearchToken | undefined {
  const searchQueryParts = splitSearchQuery(query);

  const activePartIndex = searchQueryParts.findIndex((part) =>
    Boolean(part.startOffset <= offset && offset <= part.endOffset),
  );

  // `token` does not exist in a search query part only if we're matching on space(s).
  // If this is the case, try to fall back to the next search query part, or the previous one.
  return (
    searchQueryParts[activePartIndex]?.token ||
    searchQueryParts[activePartIndex + 1]?.token ||
    searchQueryParts[activePartIndex - 1]?.token
  );
}

export const findMemoizedActiveToken = memoize()(findActiveToken);

export function isValidSearchModifierToken(token: SearchModifierToken): boolean {
  const {modifier, value} = token;

  return isValidValueForModifier(modifier, value);
}
